// Client-side validation against the contract's own constraint keywords (src/api/schemas.ts, generated).
// It only pre-empts what the API would refuse with 400; the API stays the authority.
import { schemas, type JsonSchema } from './schemas';

export interface FieldError {
  /** JSON path, e.g. `components.apacheHosts[0]` */
  path: string;
  message: string;
}

function resolve(s: JsonSchema): JsonSchema {
  let cur = s;
  while (cur.$ref) {
    const name = cur.$ref.replace('#/components/schemas/', '');
    const target = schemas[name];
    if (!target) throw new Error(`unknown schema ${cur.$ref}`);
    // A $ref with siblings (3.1) keeps the siblings' constraints too.
    const { $ref: _ref, ...rest } = cur;
    cur = { ...target, ...rest };
  }
  return cur;
}

function join(path: string, key: string | number): string {
  return typeof key === 'number' ? `${path}[${key}]` : path ? `${path}.${key}` : key;
}

export function validate(value: unknown, schema: JsonSchema | string, path = ''): FieldError[] {
  const s = resolve(typeof schema === 'string' ? { $ref: `#/components/schemas/${schema}` } : schema);
  const errors: FieldError[] = [];
  const err = (message: string) => errors.push({ path, message });

  for (const part of s.allOf ?? []) errors.push(...validate(value, part, path));
  if (value === undefined || value === null) return errors;

  switch (s.type) {
    case 'string':
      if (typeof value !== 'string') { err('must be text'); return errors; }
      if (s.minLength !== undefined && value.length < s.minLength) err(`must be at least ${s.minLength} characters`);
      if (s.maxLength !== undefined && value.length > s.maxLength) err(`must be at most ${s.maxLength} characters`);
      if (s.pattern !== undefined && !new RegExp(s.pattern, 'u').test(value)) err(`"${value}" does not match ${s.pattern}`);
      if (s.format === 'uri' && !isUri(value)) err(`"${value}" is not a URL`);
      break;
    case 'integer':
    case 'number':
      if (typeof value !== 'number' || (s.type === 'integer' && !Number.isInteger(value))) { err(`must be ${s.type === 'integer' ? 'a whole number' : 'a number'}`); return errors; }
      if (s.minimum !== undefined && value < s.minimum) err(`must be at least ${s.minimum}`);
      if (s.maximum !== undefined && value > s.maximum) err(`must be at most ${s.maximum}`);
      break;
    case 'boolean':
      if (typeof value !== 'boolean') err('must be true or false');
      break;
    case 'array':
      if (!Array.isArray(value)) { err('must be a list'); return errors; }
      if (s.minItems !== undefined && value.length < s.minItems) err(`needs at least ${s.minItems} ${s.minItems === 1 ? 'entry' : 'entries'}`);
      if (s.maxItems !== undefined && value.length > s.maxItems) err(`allows at most ${s.maxItems} entries`);
      if (s.items) value.forEach((v, i) => errors.push(...validate(v, s.items!, join(path, i))));
      break;
    case 'object':
    default:
      if (s.type === 'object' && (typeof value !== 'object' || Array.isArray(value))) { err('must be an object'); return errors; }
      if (typeof value === 'object' && !Array.isArray(value)) {
        const obj = value as Record<string, unknown>;
        for (const r of s.required ?? []) {
          if (obj[r] === undefined || obj[r] === '') errors.push({ path: join(path, r), message: 'is required' });
        }
        for (const [k, sub] of Object.entries(s.properties ?? {})) {
          if (obj[k] !== undefined) errors.push(...validate(obj[k], sub, join(path, k)));
        }
      }
  }
  if (s.enum && typeof value === 'string' && !s.enum.includes(value)) err(`must be one of ${s.enum.join(', ')}`);
  return errors;
}

function isUri(v: string): boolean {
  try {
    new URL(v);
    return true;
  } catch {
    return false;
  }
}

/** Errors whose path is `prefix` or below it. */
export function errorsAt(errors: FieldError[], prefix: string): FieldError[] {
  return errors.filter((e) => e.path === prefix || e.path.startsWith(prefix + '.') || e.path.startsWith(prefix + '['));
}
